// src/pages/productos/EditarProducto.js
import React, { useState, useEffect, useRef, useMemo } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { Offcanvas, Alert } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import { FileUpload, FilePreview } from 'components/FileUpload'
import RichTextFieldModal from 'components/RichText/RichTextFieldModal'
import Creatable from 'react-select/creatable'
import api from 'api/api'
import { toast } from 'react-toastify'
import useNumberInput from 'hooks/useNumberInput'
import { useFileUpload } from 'hooks/useFileUpload'
import {
	HABILITAR_FICHA_TECNICA,
	MODULO_PROMOCIONES,
	MANEJO_CANTIDADES,
	MAX_IMAGENES,
	HABILITAR_ATRIBUTOS
} from 'config/productos'
import { API_UPLOADS_URL } from 'config/general'
import ProductoAtributosEditor from 'components/ProductoAtributosEditor'

function EditarProducto(props) {
	const { mostrar, productoOriginal, categorias, guardar, cancelar } = props

	const [loading, setLoading] = useState(false)
	const [erroresValidacion, setErroresValidacion] = useState({})
	const [fichaTecnicaActual, setFichaTecnicaActual] = useState(null)
	const [fichaTecnicaSeleccionada, setFichaTecnicaSeleccionada] = useState(null)
	const [fichaTecnicaAEliminar, setFichaTecnicaAEliminar] = useState(false)

	const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState([])
	const [tienePrecio, setTienePrecio] = useState(false)
	const precioInput = useNumberInput((value) => handleChange(value, 'precio'))
	const precioPromocionInput = useNumberInput((value) => handleChange(value, 'precioPromocion'))
	const [datosProducto, setDatosProducto] = useState({})

	// Estados para atributos
	const [atributosCompletos, setAtributosCompletos] = useState([])
	const [atributosValores, setAtributosValores] = useState({})
	const [atributosValoresOriginales, setAtributosValoresOriginales] = useState({})
	const [tiposDirectosIds, setTiposDirectosIds] = useState([])
	const [tiposDirectosIdsOriginales, setTiposDirectosIdsOriginales] = useState([])
	const [tiposDisponibles, setTiposDisponibles] = useState([])
	const [loadingAtributos, setLoadingAtributos] = useState(false)

	const fileUpload = useFileUpload({
		maxFiles: MAX_IMAGENES,
		maxFileSize: 5000000,
		allowedTypes: ['image/jpeg', 'image/png', 'image/webp', '.jpg', '.jpeg', '.png', '.webp'],
		endpoint: 'a/multimedia',
		fieldName: 'foto',
		apiUrlPrefix: API_UPLOADS_URL,
		uploadOnSelect: false
	})

	const imagenesInicializadasRef = useRef(false)

	const esDestacado = (producto) => {
		return producto.destacado === '1' || producto.destacado === 1 || producto.destacado === true
	}

	const esActivo = (producto) => {
		return producto.activo === '1' || producto.activo === 1 || producto.activo === true
	}

	const opcionesCategorias = categorias.map(cat => ({
		label: cat.nombre,
		value: cat.id
	}))

	// Cargar atributos del producto desde el servidor
	const cargarAtributosProducto = async (itemId) => {
		if (!HABILITAR_ATRIBUTOS) return

		setLoadingAtributos(true)
		try {
			// Cargar atributos del producto
			const { datos } = await api.get(`a/items/${itemId}/atributos`)

			// Atributos completos (heredados + directos)
			setAtributosCompletos(datos.atributos || [])

			// Valores seleccionados
			const valoresSeleccionados = datos.valoresSeleccionados || {}
			setAtributosValores(valoresSeleccionados)
			setAtributosValoresOriginales(JSON.parse(JSON.stringify(valoresSeleccionados)))

			// Tipos directos
			const directosIds = datos.tiposDirectosIds || []
			setTiposDirectosIds(directosIds)
			setTiposDirectosIdsOriginales([...directosIds])

			// Cargar tipos disponibles para agregar
			try {
				const { datos: datosTipos } = await api.post('a/ver-atributos-tipos', {
					filtros: { activo: true }
				})
				setTiposDisponibles(datosTipos.tipos || [])
			} catch (error) {
				// Si falla, intentar con endpoint alternativo
				try {
					const { datos: datosTipos } = await api.get('a/atributos-tipos')
					setTiposDisponibles(datosTipos.tipos || [])
				} catch (e) {
					console.error('Error al cargar tipos disponibles:', e)
					setTiposDisponibles([])
				}
			}

		} catch (error) {
			console.error('Error al cargar atributos del producto:', error)
		} finally {
			setLoadingAtributos(false)
		}
	}

	useEffect(() => {
		if (productoOriginal && mostrar) {
			const productoTienePrecio = productoOriginal.precio && parseFloat(productoOriginal.precio) > 0
			const productoEsDestacado = esDestacado(productoOriginal)
			const productoEsActivo = esActivo(productoOriginal)

			setTienePrecio(productoTienePrecio)
			setDatosProducto({
				id: productoOriginal.id,
				nombre: productoOriginal.nombre || '',
				codigo: productoOriginal.codigo || '',
				marca: productoOriginal.marca || '',
				orden: productoOriginal.orden || 0,
				descripcion: productoOriginal.descripcion,
				destacado: productoEsDestacado,
				activo: productoEsActivo,
				precio: productoTienePrecio ? productoOriginal.precio : '',
				precioPromocion: productoTienePrecio && productoOriginal.precioPromocion ? productoOriginal.precioPromocion : '',
				cantidad: MANEJO_CANTIDADES ? (productoOriginal.cantidad || 0) : null,
				multimediaIds: productoOriginal.multimedia ? productoOriginal.multimedia.map(m => m.id) : [],
				jerarquiasIds: productoOriginal.jerarquias ? productoOriginal.jerarquias.map(j => j.id) : [],
				fichaTecnicaId: productoOriginal.fichaTecnica ? productoOriginal.fichaTecnica.id : null
			})

			if (productoOriginal.jerarquias && productoOriginal.jerarquias.length > 0) {
				const catSeleccionadas = productoOriginal.jerarquias.map(jer => ({
					label: jer.nombre,
					value: jer.id
				}))
				setCategoriasSeleccionadas(catSeleccionadas)
			} else {
				setCategoriasSeleccionadas([])
			}

			// Inicializar imágenes solo si no se han inicializado previamente
			if (!imagenesInicializadasRef.current) {
				if (productoOriginal.multimedia && productoOriginal.multimedia.length > 0) {
					const imagenes = productoOriginal.multimedia.map(imagen => ({
						id: imagen.id,
						url: imagen.url,
						nombre: imagen.nombre || `imagen_${imagen.id}`,
						type: 'image/jpeg'
					}))
					fileUpload.initializeWithExistingFiles(imagenes)
				} else {
					fileUpload.reset()
				}
				imagenesInicializadasRef.current = true
			}

			if (HABILITAR_FICHA_TECNICA) {
				if (productoOriginal.fichaTecnica) {
					setFichaTecnicaActual({
						id: productoOriginal.fichaTecnica.id,
						url: productoOriginal.fichaTecnica.url,
						nombre: productoOriginal.fichaTecnica.nombre || 'ficha_tecnica.pdf'
					})
				} else {
					setFichaTecnicaActual(null)
				}

				setFichaTecnicaSeleccionada(null)
				setFichaTecnicaAEliminar(false)
			}

			// Cargar atributos del producto desde servidor
			if (HABILITAR_ATRIBUTOS && productoOriginal.id) {
				cargarAtributosProducto(productoOriginal.id)
			}
		}
	}, [productoOriginal, mostrar])

	// Resetear el ref cuando el componente se cierra
	useEffect(() => {
		if (!mostrar) {
			imagenesInicializadasRef.current = false
			setAtributosCompletos([])
			setAtributosValores({})
			setAtributosValoresOriginales({})
			setTiposDirectosIds([])
			setTiposDirectosIdsOriginales([])
			setTiposDisponibles([])
		}
	}, [mostrar])

	const handleClose = () => {
		fileUpload.reset()
		setFichaTecnicaSeleccionada(null)
		setFichaTecnicaAEliminar(false)
		imagenesInicializadasRef.current = false
		setAtributosCompletos([])
		setAtributosValores({})
		setAtributosValoresOriginales({})
		setTiposDirectosIds([])
		setTiposDirectosIdsOriginales([])
		setTiposDisponibles([])
		cancelar()
	}

	const handleChange = (value, key) => {
		setDatosProducto(prev => ({ ...prev, [key]: value }))
	}

	// Verificar si los atributos cambiaron (valores o tipos directos)
	const atributosCambiaron = useMemo(() => {
		const originalValores = JSON.stringify(atributosValoresOriginales)
		const actualValores = JSON.stringify(atributosValores)
		const originalTipos = JSON.stringify([...tiposDirectosIdsOriginales].sort())
		const actualTipos = JSON.stringify([...tiposDirectosIds].sort())
		return originalValores !== actualValores || originalTipos !== actualTipos
	}, [atributosValoresOriginales, atributosValores, tiposDirectosIdsOriginales, tiposDirectosIds])

	const handleProcesar = async (e) => {
		if (e) e.preventDefault()

		if (validarDatos()) {
			setLoading(true)

			try {
				let fichaTecnicaId = null

				if (HABILITAR_FICHA_TECNICA) {
					fichaTecnicaId = datosProducto.fichaTecnicaId
					if (fichaTecnicaAEliminar) {
						fichaTecnicaId = null
					} else if (fichaTecnicaSeleccionada) {
						const formData = new FormData()
						formData.append('documento', fichaTecnicaSeleccionada)
						const { datos } = await api.post('a/documentos', formData)
						fichaTecnicaId = datos.id
					}
				}

				const resultadosSubida = await fileUpload.uploadFiles()
				const nuevosMultimediaIds = resultadosSubida
					.filter(r => r.success)
					.map(r => r.id)

				const enviar = { ...datosProducto }

				// Solo incluir fichaTecnicaId si la ficha técnica está habilitada
				if (HABILITAR_FICHA_TECNICA) {
					enviar.fichaTecnicaId = fichaTecnicaId
				}

				if (!tienePrecio) {
					enviar.precio = 0
					enviar.precioPromocion = null
				} else if (enviar.precioPromocion === 0 || enviar.precioPromocion === '0' || !enviar.precioPromocion) {
					enviar.precioPromocion = null
				}

				const idsAEliminar = fileUpload.getFilesToDeleteIds()
				if (idsAEliminar.length > 0) {
					enviar.eliminarMultimediaIds = idsAEliminar
				}

				if (nuevosMultimediaIds.length > 0) {
					enviar.agregarMultimediaIds = nuevosMultimediaIds
				}

				if (HABILITAR_FICHA_TECNICA && fichaTecnicaAEliminar && datosProducto.fichaTecnicaId) {
					enviar.eliminarFichaTecnica = true
				}

				// Agregar atributos si están habilitados y cambiaron
				if (HABILITAR_ATRIBUTOS && atributosCambiaron) {
					enviar.atributosValores = atributosValores
				}

				await guardar(enviar)
			} catch (error) {
				console.error('Error al guardar producto:', error)
				toast.error('Error al guardar el producto')
			} finally {
				setLoading(false)
			}
		}
	}

	const validarDatos = () => {
		const nuevosErrores = {}

		if (!datosProducto.nombre || datosProducto.nombre.trim() === '') {
			nuevosErrores.nombre = 'El nombre es obligatorio'
		}

		if (tienePrecio) {
			if (!datosProducto.precio || isNaN(datosProducto.precio) || parseInt(datosProducto.precio) <= 0) {
				nuevosErrores.precio = 'Debe ingresar un precio válido mayor a cero'
			}

			if (MODULO_PROMOCIONES && datosProducto.precioPromocion) {
				if (isNaN(datosProducto.precioPromocion) || parseInt(datosProducto.precioPromocion) <= 0) {
					nuevosErrores.precioPromocion = 'El precio de promoción debe ser mayor a cero'
				} else if (parseInt(datosProducto.precioPromocion) >= parseInt(datosProducto.precio)) {
					nuevosErrores.precioPromocion = 'El precio de promoción debe ser menor al precio regular'
				}
			}
		}

		if (MANEJO_CANTIDADES && (isNaN(datosProducto.cantidad) || parseInt(datosProducto.cantidad) < 0)) {
			nuevosErrores.cantidad = 'La cantidad debe ser un número positivo'
		}

		setErroresValidacion(nuevosErrores)
		return Object.keys(nuevosErrores).length === 0
	}

	const handleCambioCategoria = (categoriasNuevas) => {
		setCategoriasSeleccionadas(categoriasNuevas || [])
		const jerarquiasIds = (categoriasNuevas || []).map(cat => cat.value)
		handleChange(jerarquiasIds, 'jerarquiasIds')

		// Cuando cambian las categorías, recargar atributos
		if (HABILITAR_ATRIBUTOS && datosProducto.id) {
			// Los atributos heredados se actualizarán cuando se guarde
			toast.info('Los atributos heredados se actualizarán al guardar el producto', {
				autoClose: 3000
			})
		}
	}

	const handleFichaTecnica = (event) => {
		if (!HABILITAR_FICHA_TECNICA) return

		const archivo = event.target.files[0]
		if (!archivo) return

		if (archivo.type !== 'application/pdf') {
			toast.error('Solo se permiten archivos PDF como ficha técnica')
			return
		}

		if (archivo.size > 5000000) {
			toast.error('El archivo PDF excede el tamaño máximo de 5MB')
			return
		}

		setFichaTecnicaSeleccionada(archivo)
		setFichaTecnicaAEliminar(false)
	}

	const eliminarFichaTecnica = () => {
		setFichaTecnicaSeleccionada(null)
		setFichaTecnicaAEliminar(true)
		if (fichaTecnicaActual) {
			setFichaTecnicaActual(null)
		}
	}

	// Manejar cambios en valores de atributos
	const handleAtributosChange = (nuevosValores) => {
		setAtributosValores(nuevosValores)
	}

	// Manejar cambios en tipos directos
	const handleTiposDirectosChange = async (nuevosIds) => {
		setTiposDirectosIds(nuevosIds)

		// Actualizar tipos directos en el servidor inmediatamente
		if (datosProducto.id) {
			try {
				await api.put(`a/items/${datosProducto.id}/atributos-tipos`, {
					atributosTipoIds: nuevosIds
				})

				// Recargar atributos completos
				const { datos } = await api.get(`a/items/${datosProducto.id}/atributos`)
				setAtributosCompletos(datos.atributos || [])

				toast.success('Atributos actualizados')
			} catch (error) {
				console.error('Error al actualizar tipos directos:', error)
				toast.error('Error al actualizar los atributos')
				// Revertir cambio en caso de error
				setTiposDirectosIds(tiposDirectosIdsOriginales)
			}
		}
	}

	if (!productoOriginal || !datosProducto.id) return null

	return (
		<Offcanvas
			show={mostrar}
			onHide={handleClose}
			placement="end"
			scroll="true"
			backdrop="static"
			className="producto-form"
		>
			<PanelHeader
				onClick={handleClose}
				iconClass="fa-arrow-left"
				btnText="Atrás"
				text="Editar producto"
			/>
			<Offcanvas.Body id="editar-producto">
				<Form onSubmit={handleProcesar}>
					<Row>
						<Col sm={12} md={6}>
							<h5 className="mb-3">Información básica</h5>
							<Form.Group className="mb-3" controlId="nombre">
								<Form.Label>Nombre*</Form.Label>
								<Form.Control
									type="text"
									value={datosProducto.nombre}
									onChange={(e) => handleChange(e.target.value, 'nombre')}
									isInvalid={!!erroresValidacion.nombre}
								/>
								<Form.Control.Feedback type="invalid">{erroresValidacion.nombre}</Form.Control.Feedback>
							</Form.Group>
							<Form.Group className="mb-3" controlId="codigo">
								<Form.Label>Código</Form.Label>
								<Form.Control
									type="text"
									value={datosProducto.codigo}
									onChange={(e) => handleChange(e.target.value, 'codigo')}
									isInvalid={!!erroresValidacion.codigo}
								/>
								<Form.Control.Feedback type="invalid">{erroresValidacion.codigo}</Form.Control.Feedback>
							</Form.Group>
							<Form.Group className="mb-3" controlId="marca">
								<Form.Label>Marca</Form.Label>
								<Form.Control
									type="text"
									value={datosProducto.marca || ''}
									onChange={(e) => handleChange(e.target.value, 'marca')}
									placeholder="Ej: Samsung, Apple, Nike..."
								/>
								<Form.Text className="text-muted">
									Marca o fabricante del producto (opcional).
								</Form.Text>
							</Form.Group>
							<Form.Group className="mb-3" controlId="orden">
								<Form.Label>Orden</Form.Label>
								<Form.Control
									type="number"
									min="0"
									value={datosProducto.orden}
									onChange={(e) => handleChange(e.target.value, 'orden')}
									isInvalid={!!erroresValidacion.orden}
								/>
								<Form.Control.Feedback type="invalid">{erroresValidacion.orden}</Form.Control.Feedback>
								<Form.Text className="text-muted">
									Define el orden de aparición en listados. Valores menores aparecen primero.
								</Form.Text>
							</Form.Group>
							<Form.Group className="mb-3" controlId="categorias">
								<Form.Label>Categorías</Form.Label>
								<Creatable
									value={categoriasSeleccionadas}
									onChange={handleCambioCategoria}
									options={opcionesCategorias}
									isMulti={true}
									placeholder="Seleccionar o crear categorías..."
								/>
								<Form.Text className="text-muted">
									Puedes seleccionar categorías existentes o crear nuevas escribiéndolas.
								</Form.Text>
							</Form.Group>
							<hr className="my-4" />
							<h5 className="mb-3">Estados y configuración</h5>
							<Row className="mb-3">
								<Col sm={6}>
									<Form.Check
										type="switch"
										id="destacado"
										label="Destacado"
										checked={datosProducto.destacado}
										onChange={(e) => handleChange(e.target.checked, 'destacado')}
									/>
									<Form.Text className="text-muted">
										Los productos destacados se muestran en secciones especiales.
									</Form.Text>
								</Col>
								<Col sm={6}>
									<Form.Check
										type="switch"
										id="activo"
										label="Activo"
										checked={datosProducto.activo}
										onChange={(e) => handleChange(e.target.checked, 'activo')}
									/>
									<Form.Text className="text-muted">
										Solo los productos activos se muestran en el catálogo.
									</Form.Text>
								</Col>
							</Row>
							<hr className="my-4" />
							<h5 className="mb-3">Gestión de precios</h5>
							<div className="precio-section">
								<Form.Check
									type="switch"
									id="tienePrecio"
									label="Tiene precio"
									checked={tienePrecio}
									onChange={(e) => setTienePrecio(e.target.checked)}
									className="mb-3"
								/>
								{tienePrecio && (
									<div className="campos-relacionados">
										<Row>
											<Col sm={6}>
												<Form.Group className="mb-3" controlId="precio">
													<Form.Label>Precio*</Form.Label>
													<Form.Control
														type="text"
														value={precioInput.formatDisplayValue(datosProducto.precio)}
														onChange={precioInput.handleInputChange}
														onBlur={precioInput.handleBlur}
														isInvalid={!!erroresValidacion.precio}
													/>
													<Form.Control.Feedback type="invalid">{erroresValidacion.precio}</Form.Control.Feedback>
												</Form.Group>
											</Col>
											{MODULO_PROMOCIONES && (
												<Col sm={6}>
													<Form.Group className="mb-3" controlId="precioPromocion">
														<Form.Label>Precio promoción</Form.Label>
														<Form.Control
															type="text"
															value={precioPromocionInput.formatDisplayValue(datosProducto.precioPromocion)}
															onChange={precioPromocionInput.handleInputChange}
															onBlur={precioPromocionInput.handleBlur}
															isInvalid={!!erroresValidacion.precioPromocion}
														/>
														<Form.Control.Feedback type="invalid">{erroresValidacion.precioPromocion}</Form.Control.Feedback>
													</Form.Group>
												</Col>
											)}
										</Row>
									</div>
								)}
							</div>
							{MANEJO_CANTIDADES && (
								<>
									<h5 className="mb-3 mt-4">Inventario</h5>
									<Form.Group className="mb-3" controlId="cantidad">
										<Form.Label>Cantidad en stock</Form.Label>
										<Form.Control
											type="number"
											min="0"
											step="1"
											value={datosProducto.cantidad || 0}
											onChange={(e) => handleChange(parseInt(e.target.value) || 0, 'cantidad')}
											isInvalid={!!erroresValidacion.cantidad}
										/>
										<Form.Control.Feedback type="invalid">{erroresValidacion.cantidad}</Form.Control.Feedback>
									</Form.Group>
								</>
							)}
							<hr className="my-4" />
							<h5 className="mb-3">Descripción</h5>
							<RichTextFieldModal
								value={datosProducto.descripcion}
								onChange={(contenido) => handleChange(contenido, 'descripcion')}
								label="Información detallada del producto"
							/>

							{/* SECCIÓN DE ATRIBUTOS */}
							{HABILITAR_ATRIBUTOS && (
								<>
									<hr className="my-4" />
									<h5 className="mb-3">
										<i className="fa fa-tags me-2"></i>
										Atributos / Variantes
									</h5>
									<p className="text-muted small mb-3">
										Configura qué valores de atributos están disponibles para este producto.
										Los atributos pueden heredarse de las categorías o asignarse directamente.
									</p>

									<ProductoAtributosEditor
										atributos={atributosCompletos}
										valoresSeleccionados={atributosValores}
										tiposDirectosIds={tiposDirectosIds}
										tiposDisponibles={tiposDisponibles}
										onChange={handleAtributosChange}
										onTiposDirectosChange={handleTiposDirectosChange}
										disabled={loading}
										loading={loadingAtributos}
										modo="editar"
										mostrarSelectorTipos={true}
									/>

									{atributosCambiaron && (
										<Alert variant="info" className="mt-3">
											<i className="fa fa-info-circle me-2"></i>
											Los cambios en valores de atributos se guardarán junto con el producto.
										</Alert>
									)}
								</>
							)}
						</Col>
						<Col sm={12} md={6}>
							<h5 className="mb-3">Imágenes</h5>
							<Alert variant="info">
								<i className="fa fa-info-circle me-2"></i>
								Puedes subir hasta {MAX_IMAGENES} imágenes. La primera imagen será la principal.
							</Alert>
							{fileUpload.uploadedFiles.length > 0 && (
								<FilePreview
									files={fileUpload.uploadedFiles}
									onRemove={fileUpload.markFileForDeletion}
									onUndo={fileUpload.undoFileDeletion}
									title={`Imágenes del producto (${fileUpload.totalFiles}/${MAX_IMAGENES})`}
									showFileSize={false}
									showPrincipalBadge={true}
									markedForDeletion={fileUpload.filesToDelete}
									emptyMessage="No hay imágenes guardadas."
									useImageCover={false}
								/>
							)}
							{!fileUpload.isFull && (
								<FileUpload
									title="Agregar más imágenes"
									dragDropLabel="Arrastrar a esta área o"
									buttonText="Seleccionar"
									buttonTextPlural="imágenes"
									buttonTextSingular="imagen"
									buttonIconClass="fas fa-file-upload"
									updateFiles={fileUpload.handleSelectFiles}
									maxFileSizeInBytes={5000000}
									accept="image/jpeg, image/png, image/webp"
									multiple
									showPreview={false}
								/>
							)}
							{fileUpload.selectedFiles.length > 0 && (
								<FilePreview
									files={fileUpload.selectedFiles}
									onRemove={(file) => fileUpload.markFileForDeletion(file)}
									title="Imágenes seleccionadas para subir"
									showFileSize={true}
									emptyMessage="No has seleccionado nuevas imágenes."
									useImageCover={false}
								/>
							)}
							<p className="text-muted small mt-2">
								La primera imagen será la principal y se mostrará como destacada.
							</p>
							{HABILITAR_FICHA_TECNICA && (
								<>
									<hr className="my-4" />
									<h5 className="mb-3">Ficha técnica (PDF)</h5>
									{fichaTecnicaActual && !fichaTecnicaAEliminar && (
										<div className="mb-3">
											<Alert variant="info" className="d-flex align-items-center justify-content-between">
												<div>
													<i className="fa fa-file-pdf me-2"></i>
													<a
														href={API_UPLOADS_URL + fichaTecnicaActual.url}
														target="_blank"
														rel="noopener noreferrer"
													>
														{fichaTecnicaActual.nombre || 'Ver ficha técnica actual'}
													</a>
												</div>
												<Button
													variant="outline-danger"
													size="sm"
													onClick={eliminarFichaTecnica}
												>
													<i className="fa fa-times me-1"></i>
													Eliminar
												</Button>
											</Alert>
										</div>
									)}
									{fichaTecnicaSeleccionada && (
										<div className="mb-3">
											<Alert variant="warning" className="d-flex align-items-center justify-content-between">
												<div>
													<i className="fa fa-file-pdf me-2"></i>
													{fichaTecnicaSeleccionada.name} ({(fichaTecnicaSeleccionada.size / 1024 / 1024).toFixed(2)} MB)
												</div>
												<Button
													variant="outline-danger"
													size="sm"
													onClick={() => setFichaTecnicaSeleccionada(null)}
												>
													<i className="fa fa-times me-1"></i>
													Eliminar
												</Button>
											</Alert>
										</div>
									)}
									{(!fichaTecnicaActual || fichaTecnicaAEliminar) && !fichaTecnicaSeleccionada && (
										<Form.Group controlId="fichaTecnica" className="mb-3">
											<Form.Label>Seleccionar archivo PDF</Form.Label>
											<Form.Control
												type="file"
												onChange={handleFichaTecnica}
												accept="application/pdf"
											/>
											<Form.Text className="text-muted">
												Tamaño máximo: 5MB. El archivo se subirá al guardar el producto.
											</Form.Text>
										</Form.Group>
									)}
								</>
							)}
						</Col>
					</Row>
				</Form>
			</Offcanvas.Body>
			<PanelFooter>
				<Button
					variant="outline-secondary"
					onClick={handleClose}
					className="me-2"
					disabled={loading || fileUpload.isLoading}
				>
					Cancelar
				</Button>
				<Button
					variant="primary"
					onClick={handleProcesar}
					disabled={loading || fileUpload.isLoading}
				>
					{(loading || fileUpload.isLoading) ? (
						<><i className="fa fa-spinner fa-spin me-2"></i> Guardando...</>
					) : 'Guardar cambios'}
				</Button>
			</PanelFooter>
		</Offcanvas>
	)
}

export default EditarProducto